import { Injectable } from '@angular/core'
import { InfoDialogService, isSomething } from '@tokeet-frontend/tv3-platform'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import * as lodash from 'lodash'
import { AirbnbStandardFeeType } from '@tokeet-frontend/channels'
import { DecimalPipe } from '@angular/common'
import { VRBOPriceItem, TripPrice, AirbnbPrice, MarriottFeeTaxDetail } from '@tv3/store/inquiry/channel_price.model'

@Injectable()
export class InquiryChannelBreakdownDialogsService {
  numberPipe = new DecimalPipe('en-US')
  constructor(private infoDialogService: InfoDialogService) {}

  toFixed(v: number) {
    return this.numberPipe.transform(v || 0, '1.2-2')
  }

  hasChannelPrice(inquiry: Inquiry) {
    return (
      isSomething(inquiry.abb_price) ||
      isSomething(inquiry.expedia_price) ||
      isSomething(inquiry.bdc_price) ||
      isSomething(inquiry.marriott_price) ||
      isSomething(inquiry.vrbo_price || inquiry.price_details) ||
      isSomething(inquiry.tiket_price) ||
      // isSomething(inquiry.rentalsunited_price) ||
      isSomething(inquiry.agoda_price) ||
      (isSomething(inquiry.price_details) && /trip\.com/i.test(inquiry.inquirySource))
    )
  }

  onChannelBreakdown(inquiry: Inquiry, currency: string) {
    let breakdown = ''
    if (isSomething(inquiry.abb_price)) {
      breakdown = this.onAirBnBBreakdown(inquiry, currency)
    } else if (isSomething(inquiry.agoda_price)) {
      breakdown = this.onAgodaBreakdown(inquiry, currency)
    } else if (isSomething(inquiry.expedia_price)) {
      breakdown = this.onExpediaBreakdown(inquiry, currency)
    } else if (isSomething(inquiry.bdc_price)) {
      breakdown = this.onBDCBreakdown(inquiry, currency)
    } else if (isSomething(inquiry.marriott_price)) {
      breakdown = this.onMarriottBreakdown(inquiry)
    } else if (isSomething(inquiry.vrbo_price || inquiry.price_details)) {
      breakdown = this.onVrboBreakdown(inquiry)
    } else if (isSomething(inquiry.tiket_price)) {
      breakdown = this.onTiketBreakdown(inquiry)
    } else if (isSomething(inquiry.rentalsunited_price)) {
      breakdown = this.onRentalsUnitedBreakdown(inquiry)
    } else if (
      isSomething(inquiry.ctrip_price) ||
      (isSomething(inquiry.price_details) && /trip\.com/i.test(inquiry.inquirySource))
    ) {
      breakdown = this.onTripComBreakdown(inquiry)
    }

    if (!breakdown) {
      return
    }

    this.infoDialogService.open({
      title: 'Charge Detail',
      body: breakdown,
      closeText: 'Close',
    })
  }

  onAirBnBBreakdown(inquiry: Inquiry, currency: string) {
    if (!isSomething(inquiry.abb_price)) {
      return
    }
    const abb_price = inquiry.abb_price

    const isCleaningFee = (fee: any) => /cleaning_fee/i.test(fee.fee_type || '')

    const [cleaningFees, otherFees]: AirbnbPrice['standard_fees'][] = lodash.partition(
      abb_price.standard_fees,
      isCleaningFee
    )

    const fees = lodash.map(otherFees, (fee) => {
      const name = lodash.startCase(lodash.toLower(fee.fee_type?.replace(/^pass_through/gi, '')))
      const amount = fee.amount_native || fee.amount || 0
      if (!name || !amount) return ''
      return `
      <tr>
        <td>${name}:</td>
        <td align="right">+${currency}${this.toFixed(amount)}</td>
      </tr>
    `
    })
    const cleaningFeeTotal = lodash.sumBy(cleaningFees, (fee) => fee.amount_native || 0)
    const taxesTotal = abb_price.tax || 0 // lodash.sumBy(abb_price.transient_occupancy_tax, (tax) => tax.amount_native || 0)
    const total = (abb_price.payout || 0) + (abb_price.fees || 0)
    const airbnbCollectedTaxTotal = abb_price.airbnb_collected_tax || 0

    return `
      <div class="row">
        <div class="offset-1 col-sm-10">
          <table border=0 class="w-100">
            <tr>
              <td>Base charge:</td>
              <td align="right" width=120>${currency}${this.toFixed(abb_price.base)}</td>
            </tr>
            <tr>
              <td>Cleaning Fee:</td>
              <td align="right">+${currency}${this.toFixed(cleaningFeeTotal)}</td>
            </tr>
            ${fees.join('')}
            <tr>
              <td>Taxes:</td>
              <td align="right">+${currency}${this.toFixed(taxesTotal)}</td>
            </tr>
            <tr>
              <td>Airbnb Fee:</td>
              <td align="right">-${currency}${this.toFixed(abb_price.fees)}</td>
            </tr>
            <tr>
              <td colspan=2><hr noshade width=50% /></td>
            </tr>
            <tr>
              <td>Guest Paid:</td>
              <td align="right">${currency}${this.toFixed(total)}</td>
            </tr>
            <tr>
              <td>Host Payout:</td>
              <td align="right">${currency}${this.toFixed(abb_price.payout)}</td>
            </tr>
            <tr>
              <td colspan=2><hr noshade width=50% /></td>
            </tr>
            <tr>
              <td>Airbnb Collected Tax:</td>
              <td align="right">${currency}${this.toFixed(airbnbCollectedTaxTotal)}</td>
            </tr>
            <tr>
              <td colspan="2">
                <div class="alert alert-secondary-warning2 text-xs px-2 py-1">
                  <i class="fa fa-info-circle"></i> This tax is part of booking base charge
                </div>
              </td>
            </tr>
          </table>
        </div>
      </div>
    `
  }

  onAgodaBreakdown(inquiry: Inquiry, currency: string) {
    if (!isSomething(inquiry.agoda_price)) {
      return
    }

    const agoda_price = inquiry.agoda_price
    const taxesTotal = (agoda_price.refsell || 0) - (agoda_price.before_taxes || 0)

    return `
      <div class="row">
        <div class="offset-1 col-sm-10">
          <table border=0 class="w-100">
            <tr>
              <td>Base Charge:</td>
              <td align="right">${currency}${this.toFixed(agoda_price.before_taxes)}</td>
            </tr>
            <tr>
              <td>Taxes:</td>
              <td align="right">+${currency}${this.toFixed(taxesTotal)}</td>
            </tr>
            <tr>
              <td colspan=2><hr noshade width=50% /></td>
            </tr>
            <tr>
              <td>Payout:</td>
              <td align="right">${currency}${this.toFixed(agoda_price.net_inclusive)}</td>
            </tr>
          </table>
        </div>
      </div>
    `
  }

  onExpediaBreakdown(inquiry: Inquiry, currency: string) {
    if (!isSomething(inquiry.expedia_price)) {
      return
    }

    const expedia_price = inquiry.expedia_price
    const paymentModel = expedia_price.payment_model
    const isNet = /net/i.test(paymentModel)

    return `
      <div class="row">
        <div class="offset-1 col-sm-10">
          <table border=0 class="w-100">
           ${
             paymentModel
               ? `
           <tr>
              <td>Payment Model:</td>
              <td align="right">${paymentModel}</td>
            </tr>
           `
               : ''
           }
            <tr>
              <td>${isNet ? 'User Charge:' : 'Base Charge:'}</td>
              <td align="right" width=120>${currency}${this.toFixed(
      isNet ? expedia_price.total : expedia_price.before_taxes
    )}</td>
            </tr>
            <tr>
              <td>Commission:</td>
              <td align="right">-${currency}${this.toFixed(expedia_price.commission)}</td>
            </tr>
            <tr>
              <td>Taxes:</td>
              <td align="right">${isNet ? '-' : '+'}${currency}${this.toFixed(expedia_price.taxes)}</td>
            </tr>
            <tr>
              <td colspan=2><hr noshade width=50% /></td>
            </tr>
            <tr>
              <td>Payout:</td>
              <td align="right">${currency}${this.toFixed(
      expedia_price.payment_details?.amount || expedia_price.total
    )}</td>
            </tr>
          </table>
        </div>
      </div>
    `
  }

  onBDCBreakdown(inquiry: Inquiry, currency: string) {
    if (!isSomething(inquiry.bdc_price)) {
      return
    }
    const bdc_price = inquiry.bdc_price
    const totalFees = lodash.sumBy(
      lodash.keys(bdc_price).filter((t) => t.endsWith('fee')),
      (fee) => bdc_price[fee]
    )
    const total = bdc_price.total || (bdc_price.before_taxes || 0) + (totalFees || 0)
    const payout = total - (bdc_price.comission || 0)
    const extras = lodash
      .keys(bdc_price)
      .filter((key) => /^extra_/.test(key))
      .map((key) => {
        const name = lodash.startCase(lodash.toLower(key.replace(/^extra_/gi, '')))
        return `
        <tr>
            <td>${name}:</td>
            <td align="right">+${currency}${this.toFixed(bdc_price[key])}</td>
          </tr>
      `
      })

    return `
    <div class="row">
      <div class="offset-1 col-sm-10">
        <table border=0 class="w-100">
          <tr>
            <td>Base Charge:</td>
            <td align="right" width=120>${currency}${this.toFixed(bdc_price.before_taxes)}</td>
          </tr>
          <tr>
            <td>Commission:</td>
            <td align="right">-${currency}${this.toFixed(bdc_price.comission)}</td>
          </tr>
          <tr>
            <td>Taxes:</td>
            <td align="right">+${currency}${this.toFixed(bdc_price.taxes)}</td>
          </tr>
          ${extras.join('')}
          <tr>
            <td colspan=2><hr noshade width=50% /></td>
          </tr>
          <tr>
            <td>Guest Payout:</td>
            <td align="right">${currency}${this.toFixed(total)}</td>
          </tr>
          <tr>
            <td>Host Payout:</td>
            <td align="right">${currency}${this.toFixed(payout)}</td>
          </tr>
        </table>
      </div>
    </div>
  `
  }

  onMarriottBreakdown(inquiry: Inquiry) {
    if (!isSomething(inquiry.marriott_price)) {
      return
    }
    const marriottPrice = inquiry.marriott_price
    const currency = marriottPrice.currency
    const total = marriottPrice.total
    const feeTaxDetails = marriottPrice.fees_taxes_details
    const detailedFees = lodash
      .chain(feeTaxDetails?.fees || [])
      .map((item) => this.marriottDetailRow(item, currency, 'Fee'))
      .value()
    const detailedTaxes = lodash
      .chain(feeTaxDetails?.taxes || [])
      .map((item) => this.marriottDetailRow(item, currency, 'Tax'))
      .value()
    const hasDetailedBreakdown = detailedFees.length > 0 || detailedTaxes.length > 0
    const legacyFees = lodash
      .chain(marriottPrice.fees)
      .map((item) => {
        return `
        <tr>
            <td>${item.description}:</td>
            <td align="right">+${currency}${this.toFixed(item.amount)}</td>
          </tr>
      `
      })
      .value()
    const rows = hasDetailedBreakdown ? detailedFees.concat(detailedTaxes) : legacyFees

    // The discount is not a line item in the HVMI model; it is total - discounted_total.
    // Older persisted prices used discounted_amount for the same discounted guest total.
    let discountedTotal = total
    if (Number.isFinite(marriottPrice.discounted_total)) {
      discountedTotal = marriottPrice.discounted_total
    } else if (Number.isFinite(marriottPrice.discounted_amount)) {
      discountedTotal = marriottPrice.discounted_amount
    }
    const hasDiscount = discountedTotal < total
    const discount = hasDiscount ? total - discountedTotal : 0
    const discountRow = hasDiscount
      ? `
          <tr>
            <td>Discount:</td>
            <td align="right">-${currency}${this.toFixed(discount)}</td>
          </tr>
      `
      : ''
    // Guest pays the discounted total. HVMI computes commission on that discounted
    // base, net of any HVMI-funded discount contribution (commission_less_contribution_amount).
    const guestTotal = hasDiscount ? discountedTotal : total
    const payout =
      guestTotal -
      (marriottPrice.commissions_info?.commission_less_contribution_amount || 0) -
      (marriottPrice.commissions_info?.hvmi_payment_fee || 0)

    return `
    <div class="row">
      <div class="offset-1 col-sm-10">
        <table border=0 class="w-100">
          <tr>
            <td>Base Charge:</td>
            <td align="right" width=120>${currency}${this.toFixed(marriottPrice.nightly_base_price)}</td>
          </tr>
          ${discountRow}
          <tr>
            <td>Commission %:</td>
            <td align="right">${marriottPrice.commissions_info?.commission_rate || 0}%</td>
          </tr>
          <tr>
            <td>Commission:</td>
            <td align="right">-${currency}${this.toFixed(marriottPrice.commissions_info?.commission_amount)}</td>
          </tr>
          <tr>
            <td>Comm. Less Contrib.:</td>
            <td align="right">-${currency}${this.toFixed(
      marriottPrice.commissions_info?.commission_less_contribution_amount
    )}</td>
          </tr>
          <tr>
            <td>Discount Contrib.:</td>
            <td align="right">-${currency}${this.toFixed(
      marriottPrice.commissions_info?.hvmi_discount_contribution
    )}</td>
          </tr>
          <tr>
            <td>Payment Fee:</td>
            <td align="right">-${currency}${this.toFixed(marriottPrice.commissions_info?.hvmi_payment_fee)}</td>
          </tr>
          ${rows.join('')}
          <tr>
            <td colspan=2><hr noshade width=50% /></td>
          </tr>
          <tr>
            <td>Payout:</td>
            <td align="right">${currency}${this.toFixed(payout)}</td>
          </tr>
        </table>
      </div>
    </div>
  `
  }

  private marriottDetailRow(item: MarriottFeeTaxDetail, currency: string, defaultLabel: string) {
    const label = item.description || item.name || defaultLabel
    // PERCENT entries carry the rate in price_per_unit (e.g. 2 -> "2%"); FLAT
    // entries are priced per unit (price_per_unit x quantity).
    const unitText =
      item.type === 'PERCENT'
        ? item.price_per_unit != null
          ? ` (${item.price_per_unit}%)`
          : ''
        : item.price_per_unit && item.quantity
        ? ` (${this.toFixed(item.price_per_unit)} x ${item.quantity})`
        : ''
    // Fees due upon arrival are collected by the host at check-in, not via HVMI.
    const dueText = item.dueUponArrival ? ' (due at arrival)' : ''
    return `
        <tr>
            <td>${label}${unitText}${dueText}:</td>
            <td align="right">+${currency}${this.toFixed(item.amount || 0)}</td>
          </tr>
      `
  }

  onVrboBreakdown(inquiry: Inquiry) {
    if (!isSomething(inquiry.vrbo_price || inquiry.price_details)) {
      return
    }

    const data = inquiry.vrbo_price || (inquiry.price_details as VRBOPriceItem[])
    const itemsByType = lodash.groupBy(data, (item) => lodash.toLower(item.feeType))
    const rentalBaseType = 'rental'
    const baseCharge = lodash.head(itemsByType[rentalBaseType])
    const currency = baseCharge.totalAmount.currency

    const total = lodash.sumBy(data, (item) => +item.totalAmount.content || 0)

    const fees = lodash
      .chain(itemsByType)
      .omit(rentalBaseType)
      .values()
      .flatten()
      .map((item) => {
        const amount = item.totalAmount.content
        return `
        <tr>
            <td>${item.name}:</td>
            <td align="right">${amount > 0 ? '+' : '-'}${currency}${this.toFixed(Math.abs(amount))}</td>
          </tr>
      `
      })
      .value()

    return `
    <div class="row">
      <div class="offset-1 col-sm-10">
        <table border=0 class="w-100">
          <tr>
            <td>Base Charge:</td>
            <td align="right" width=120>${currency}${this.toFixed(baseCharge.totalAmount.content)}</td>
          </tr>
          ${fees.join('')}
          <tr>
            <td colspan=2><hr noshade width=50% /></td>
          </tr>
          <tr>
            <td>Payout:</td>
            <td align="right">${currency}${this.toFixed(total)}</td>
          </tr>
        </table>
      </div>
    </div>
  `
  }

  onTiketBreakdown(inquiry: Inquiry) {
    if (!isSomething(inquiry.tiket_price)) {
      return
    }

    const data = inquiry.tiket_price
    const currency = data.Total._CurrencyCode
    const total = data.Total?._AmountAfterTax || 0
    const totalBeforeTax = data.Total?._AmountBeforeTax || total || 0
    const tax = total - totalBeforeTax

    return `
    <div class="row">
      <div class="offset-1 col-sm-10">
        <table border=0 class="w-100">
          <tr>
            <td>Base Charge:</td>
            <td align="right" width=120>${currency}${this.toFixed(totalBeforeTax)}</td>
          </tr>
          <tr>
            <td>Tax:</td>
            <td align="right" width=120>${currency}${this.toFixed(tax)}</td>
          </tr>
          <tr>
            <td colspan=2><hr noshade width=50% /></td>
          </tr>
          <tr>
            <td>Payout:</td>
            <td align="right">${currency}${this.toFixed(total)}</td>
          </tr>
        </table>
      </div>
    </div>
  `
  }

  onTripComBreakdown(inquiry: Inquiry) {
    if (!isSomething(inquiry.price_details) || isSomething(inquiry.ctrip_price)) {
      return
    }

    const data = inquiry.ctrip_price || (inquiry.price_details as TripPrice)
    const currency = data.Total.CurrencyCode
    const total = data.Total?.AmountAfterTax || 0
    const totalBeforeTax = data.Total?.AmountBeforeTax || total || 0
    const tax = total - totalBeforeTax

    return `
    <div class="row">
      <div class="offset-1 col-sm-10">
        <table border=0 class="w-100">
          <tr>
            <td>Base Charge:</td>
            <td align="right" width=120>${currency}${this.toFixed(totalBeforeTax)}</td>
          </tr>
          <tr>
            <td>Tax:</td>
            <td align="right" width=120>${currency}${this.toFixed(tax)}</td>
          </tr>
          <tr>
            <td colspan=2><hr noshade width=50% /></td>
          </tr>
          <tr>
            <td>Payout:</td>
            <td align="right">${currency}${this.toFixed(total)}</td>
          </tr>
        </table>
      </div>
    </div>
  `
  }

  onRentalsUnitedBreakdown(inquiry: Inquiry) {
    if (!isSomething(inquiry.rentalsunited_price)) {
      return
    }
    const data = inquiry.rentalsunited_price
    const currency = data.Currency
    const total = data.Costs.ClientPrice || 0
    const fees = lodash
      .chain(data.ReservationBreakdown?.ChannelBreakdown?.ChannelTotalFeesTaxes?.ChannelTotalFeeTax)
      .map((item) => {
        return `
        <tr>
            <td>${item.Name}:</td>
            <td align="right">+${currency}${this.toFixed(item.Amount)}</td>
          </tr>
      `
      })
      .value()

    return `
    <div class="row">
      <div class="offset-1 col-sm-10">
        <table border=0 class="w-100">
          <tr>
            <td>Base Charge:</td>
            <td align="right" width=120>${currency}${this.toFixed(
      data.ReservationBreakdown?.ChannelBreakdown?.ChannelRent || 0
    )}</td>
          </tr>
          <tr>
            <td>Commission:</td>
            <td align="right">-${currency}${this.toFixed(data.ReservationBreakdown?.ChannelCommission || 0)}</td>
          </tr>
          ${fees.join('')}
          <tr>
            <td colspan=2><hr noshade width=50% /></td>
          </tr>
          <tr>
            <td>Payout:</td>
            <td align="right">${currency}${this.toFixed(total)}</td>
          </tr>
        </table>
      </div>
    </div>
  `
  }
}
